import type { ZudokuPlugin } from "zudoku";
import { Head, Heading, Typography } from "zudoku/components";
import * as specs from "virtual:subbly-api";
import { defineReference, isUnder, type Reference } from "../define";
import { sidebarOf } from "../navigation";
import { ApiPage } from "./ApiPage";
import { API_SOURCES, type ApiSource } from "./sources";
import type { TagDoc } from "./types";

/*
  Routes and sidebar of the API references. The pages come from the OpenAPI
  specs through the virtual modules of ./vite.ts; nothing is written to
  pages/. The overview page of each API is a hand-written MDX page at the
  base path, registered as a hidden doc in zudoku.config.tsx.
*/

const references = (): Reference[] =>
  (specs.apis ?? []).flatMap((index) => {
    const source = API_SOURCES.find((s) => s.id === index.id);
    return source ? [defineReference({ ...source, groups: index.groups })] : [];
  });

const Page = ({ data }: { data: TagDoc }) => (
  <div
    className="grid grid-cols-1 gap-8"
    data-pagefind-filter="section:api"
    data-pagefind-meta="section:api"
  >
    <Typography className="max-w-full flex-1 shrink pt-(--padding-content-top)">
      <ApiPage data={data} />
    </Typography>
  </div>
);

export const apiReferences = (): ZudokuPlugin => ({
  getRoutes: () =>
    references().flatMap((reference) =>
      reference.groups.map((group) => ({
        path: reference.pathOf(group),
        lazy: async () => {
          const key = `${API_SOURCES.find((s) => s.path === reference.path)?.id}/${group.slug}`;
          const { default: data } = await specs.loaders[key]!();
          return { element: <Page data={data} /> };
        },
      })),
    ),
  getNavigation: async (path) => {
    const reference = references().find((r) => isUnder(path, r.path));
    return reference ? sidebarOf(reference) : [];
  },
});

/* Head of an overview page; the topics follow in the MDX. */
export const ApiOverview = ({ api }: { api: ApiSource["id"] }) => {
  const source = API_SOURCES.find((s) => s.id === api);
  if (!source) throw new Error(`No API named "${api}"`);
  return (
    <>
      <Head>
        <title>{source.title}</title>
        <meta name="description" content={source.description} />
      </Head>
      <Heading level={1}>{source.title}</Heading>
    </>
  );
};
